import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Check, ArrowRight, Sparkles, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { PLAN_PRICING } from '../lib/pricing';

const ChoosePlan = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
      {/* Header */}
      <nav className="px-6 md:px-12 py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Shield className="h-8 w-8 text-white" />
            <span className="font-bold text-xl text-white">AIVORA Comply360</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm hidden sm:block">
              Logged in as {user?.email}
            </span>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-slate-300 hover:text-white hover:bg-white/10"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="px-6 md:px-12 py-12">
        <div className="max-w-4xl mx-auto">
          
          {/* Lock Icon & Message */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-500/20 rounded-full mb-6">
              <Lock className="h-10 w-10 text-amber-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Choose Your Plan to Continue
            </h1>
            <p className="text-slate-300 text-lg max-w-xl mx-auto">
              Welcome, {user?.name || 'there'}! Select a plan below to unlock your ISO 27001:2022 maturity assessments.
            </p>
            
            <div className="mt-6 inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm text-white/80" data-testid="choose-plan-currency-label">Prices shown in USD</div>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            
            {/* Standard Pack */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden"
            >
              <div className="p-8">
                <div className="text-center mb-6">
                  <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                    FOR SMALL TEAMS
                  </span>
                  <h3 className="font-bold text-2xl text-slate-900 mb-1">Standard Pack</h3>
                  <p className="text-slate-500 text-sm">Perfect for getting started</p>
                </div>
                
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-bold text-5xl text-slate-900">{PLAN_PRICING.standard.amount}</span>
                    <span className="text-slate-500">{PLAN_PRICING.standard.label}</span>
                  </div>
                  <p className="text-slate-400 text-xs mt-1">One-time payment</p>
                </div>

                <div className="space-y-3 mb-6">
                  {[
                    '1 User Included',
                    'Additional Users at $10 Each',
                    '3 Assessments',
                    'PDF & Excel Reports',
                    '24/5 Email Support'
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                        <Check className="h-3 w-3 text-green-600" />
                      </div>
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <a 
                  href={PLAN_PRICING.standard.paymentLink}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6">
                    Buy Standard - {PLAN_PRICING.standard.amount}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Pro Pack */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 rounded-2xl shadow-xl overflow-hidden relative"
            >
              <div className="absolute top-4 right-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                BEST VALUE
              </div>
              
              <div className="p-8">
                <div className="text-center mb-6">
                  <span className="inline-block bg-blue-500/20 text-blue-300 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                    FOR GROWING TEAMS
                  </span>
                  <h3 className="font-bold text-2xl text-white mb-1">Pro Pack</h3>
                  <p className="text-slate-400 text-sm">Complete compliance solution</p>
                </div>
                
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-bold text-5xl text-white">{PLAN_PRICING.pro.amount}</span>
                    <span className="text-slate-400">{PLAN_PRICING.pro.label}</span>
                  </div>
                  <p className="text-slate-500 text-xs mt-1">One-time payment</p>
                </div>

                <div className="space-y-3 mb-6">
                  {[
                    '3 Users Included',
                    'Additional Users at $20 Each',
                    '5 Assessments',
                    'Full 93 Controls Coverage',
                    'ISMS Document Control',
                    'PDF & Excel Reports',
                    '24/7 Email Support'
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-green-500/20 rounded-full flex items-center justify-center">
                        <Check className="h-3 w-3 text-green-400" />
                      </div>
                      <span className="text-white text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <a 
                  href={PLAN_PRICING.pro.paymentLink}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full bg-white text-slate-900 hover:bg-slate-100 py-6 font-semibold">
                    <Sparkles className="mr-2 h-4 w-4" />
                    Buy Pro - {PLAN_PRICING.pro.amount}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>

          {/* Already Paid Link */}
          <motion.div 
            className="text-center mt-8 space-y-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <p className="text-slate-400 text-sm">
              Secure payment via Razorpay
            </p>
            <a 
              href="/payment-success"
              className="inline-block text-blue-400 hover:text-blue-300 text-sm font-medium underline underline-offset-4"
              data-testid="already-paid-link"
            >
              Already paid? Activate your account here
            </a>
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default ChoosePlan;
